/**
 * The server's own explanation out of a rejected RTK Query call.
 *
 * Worth having wherever a refusal is something the author can act on: publishing answers
 * "Invalid scenario JSON: …" and names what is incomplete, and replacing that with a
 * generic failure would throw away the only part that says what to fix.
 *
 * Nest sends `message` as a string, or as an array when several validators failed.
 */
export const readApiErrorMessage = (error: unknown): string => {
	const data = (error as { data?: unknown })?.data;

	if (!data || typeof data !== "object") {
		return "";
	}

	const { message } = data as { message?: unknown };

	if (Array.isArray(message)) {
		return message.filter((entry) => typeof entry === "string").join(" · ");
	}

	return typeof message === "string" ? message : "";
};

/**
 * Whether the request never reached the server at all.
 *
 * Worth telling apart from a refusal, because the advice is opposite: a refused publish is
 * something the author fixes in the scenario, while an unreachable server is nothing they
 * can fix by trying again — and "try again" is exactly what a generic failure tells them
 * to do.
 */
export const isUnreachableError = (error: unknown): boolean => {
	const status = (error as { status?: unknown })?.status;

	return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
};

/** Where a phase's flow is broken — BCM360-364. `target` and `phases` are phase ids. */
export interface ScenarioProblem {
	code:
		| "UNKNOWN_TARGET"
		| "FALLBACK_NOT_AN_OPTION"
		| "NO_START"
		| "SEVERAL_STARTS"
		| "START_WITHOUT_DECISION";
	target?: number;
	phases?: number[];
}

/**
 * One phase (or the scenario) and what is wrong with it — the shape `publish` refuses with.
 * `phase` is the phase's id, the number the editor shows.
 */
export interface ScenarioIssue {
	phase?: number;
	phaseName?: string;
	fields: string[];
	problems?: ScenarioProblem[];
}

/**
 * The incomplete-scenario refusal, when that is what came back.
 *
 * The server sends which phase and which fields rather than a sentence, because the
 * sentence has to be in the reader's language and the server does not know it. Reading
 * them here is what lets the same refusal appear in German or English.
 */
export const readScenarioIssues = (error: unknown): ScenarioIssue[] | null => {
	const data = (error as { data?: unknown })?.data;

	if (!data || typeof data !== "object") {
		return null;
	}

	const { code, issues } = data as { code?: unknown; issues?: unknown };

	return code === "SCENARIO_INCOMPLETE" && Array.isArray(issues)
		? (issues as ScenarioIssue[])
		: null;
};
